(() => {
  if (window.__casebangCartDiscount) return;
  window.__casebangCartDiscount = true;

  let draftCode = '';
  let draftIsManual = false;
  let applying = false;
  let refreshTimer = null;

  const normalizeCode = (value) => String(value || '').trim().toUpperCase();
  const widgets = () => [...document.querySelectorAll('[data-cb-discount]')];

  function setInputs(code) {
    widgets().forEach((widget) => {
      const input = widget.querySelector('[data-cb-discount-input]');
      if (input && input.value !== code) input.value = code;
    });
  }

  function setMessage(widget, message) {
    const node = widget.querySelector('[data-cb-discount-message]');
    if (node) node.textContent = message;
  }

  function setApplyButton(widget, state) {
    const button = widget.querySelector('[data-cb-discount-apply]');
    if (!button) return;

    button.disabled = state === 'loading';
    button.textContent =
      state === 'loading' ? 'Applying…' :
      state === 'applied' ? 'Applied ✓' :
      'Apply';
  }

  function formatMoney(amount, currencyCode) {
    const number = Number(amount);
    if (!Number.isFinite(number)) return '';

    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format(number);

      return currencyCode ? formatted + ' ' + currencyCode : formatted;
    } catch (error) {
      return number.toFixed(2) + (currencyCode ? ' ' + currencyCode : '');
    }
  }

  function updateVisibleTotals(cart) {
    const totalAmount = Number(cart?.cost?.totalAmount?.amount);
    const currency = cart?.cost?.totalAmount?.currencyCode;

    if (!Number.isFinite(totalAmount)) return;

    widgets().forEach((widget) => {
      const scope = widget.closest('.drawer__footer, #main-cart-footer') || document;
      const total = scope.querySelector('.totals__total-value');
      if (!total) return;

      const originalCents = Number(total.dataset.originalTotalCents);
      const originalAmount = Number.isFinite(originalCents) ? originalCents / 100 : NaN;
      const hasDiscount = Number.isFinite(originalAmount) && originalAmount > totalAmount + 0.001;

      if (hasDiscount) {
        total.innerHTML =
          '<span class="cb-total-original">' +
          formatMoney(originalAmount, currency) +
          '</span>' +
          '<span class="cb-total-current">' +
          formatMoney(totalAmount, currency) +
          '</span>';
      } else {
        total.innerHTML =
          '<span class="cb-total-current">' +
          formatMoney(totalAmount, currency) +
          '</span>';
      }
    });
  }

  function discountEntry(cart, code) {
    const normalized = normalizeCode(code);
    return (cart?.discountCodes || []).find(
      (entry) => normalizeCode(entry?.code) === normalized
    );
  }

  function firstCartCode(cart) {
    const entry = (cart?.discountCodes || [])[0];
    return normalizeCode(entry?.code);
  }

  async function getCart() {
    if (!window.Shopify?.actions?.getCart) return null;
    const result = await Shopify.actions.getCart();
    return result?.cart || null;
  }

  async function updateCartDiscount(code) {
    if (!window.Shopify?.actions?.updateCart) {
      throw new Error('Shopify cart actions are unavailable.');
    }

    const result = await Shopify.actions.updateCart({
      discountCodes: code ? [code] : []
    });

    if (result?.userErrors?.length) {
      throw new Error(result.userErrors[0].message || 'Unable to update discount code.');
    }

    return result?.cart || null;
  }

  function renderCart(cart, options = {}) {
    if (!cart) return;

    updateVisibleTotals(cart);

    const cartCode = firstCartCode(cart);

    if (!draftIsManual) {
      draftCode = cartCode;
      setInputs(draftCode);
    }

    widgets().forEach((widget) => {
      const entry = draftCode ? discountEntry(cart, draftCode) : null;

      if (draftIsManual) {
        setApplyButton(widget, 'idle');
        setMessage(
          widget,
          draftCode ? 'Tap Apply to validate and replace the current code.' : 'Tap Apply to remove the current code.'
        );
        return;
      }

      if (!draftCode) {
        setApplyButton(widget, 'idle');
        setMessage(widget, options.cartChanged ? 'Cart updated.' : 'Enter a code, then tap Apply.');
        return;
      }

      if (entry?.applicable !== false) {
        setApplyButton(widget, 'applied');
        setMessage(widget, draftCode + ' is active on this cart.');
      } else {
        setApplyButton(widget, 'idle');
        setMessage(
          widget,
          options.cartChanged
            ? draftCode + ' is no longer valid for this cart.'
            : 'This code is not valid for the current cart.'
        );
      }
    });
  }

  async function refresh(options = {}) {
    if (applying) return;

    try {
      const cart = await getCart();
      renderCart(cart, options);
    } catch (error) {
      // Keep the cart usable even if discount-state refresh fails.
    }
  }

  async function applyCode(code) {
    if (applying) return false;

    applying = true;
    widgets().forEach((widget) => {
      setApplyButton(widget, 'loading');
      setMessage(widget, code ? "We're validating this code…" : 'Removing discount code…');
    });

    try {
      const cart = await updateCartDiscount(code);

      draftCode = code;
      draftIsManual = false;
      setInputs(draftCode);
      updateVisibleTotals(cart);

      if (!code) {
        widgets().forEach((widget) => {
          setApplyButton(widget, 'idle');
          setMessage(widget, 'Discount code removed.');
        });
        return true;
      }

      const entry = discountEntry(cart, code);
      const valid = Boolean(entry && entry.applicable !== false);

      widgets().forEach((widget) => {
        setApplyButton(widget, valid ? 'applied' : 'idle');
        setMessage(
          widget,
          valid ? code + ' is active on this cart.' : 'This code is not valid for the current cart.'
        );
      });

      return valid;
    } catch (error) {
      widgets().forEach((widget) => {
        setApplyButton(widget, 'idle');
        setMessage(widget, error.message || 'Unable to apply this code.');
      });
      return false;
    } finally {
      applying = false;
    }
  }

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      return copied;
    }
  }

  document.addEventListener('input', (event) => {
    const input = event.target instanceof Element
      ? event.target.closest('[data-cb-discount-input]')
      : null;

    if (!input) return;

    draftCode = normalizeCode(input.value);
    draftIsManual = true;
    setInputs(draftCode);

    widgets().forEach((widget) => {
      setApplyButton(widget, 'idle');
      setMessage(
        widget,
        draftCode ? 'Tap Apply to validate and replace the current code.' : 'Tap Apply to remove the current code.'
      );
    });
  });

  document.addEventListener('keydown', async (event) => {
    const input = event.target instanceof Element
      ? event.target.closest('[data-cb-discount-input]')
      : null;

    if (!input || event.key !== 'Enter') return;

    event.preventDefault();
    await applyCode(normalizeCode(input.value));
  });

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const copyButton = target.closest('[data-cb-discount-copy]');
    if (copyButton) {
      event.preventDefault();

      const code = normalizeCode(copyButton.getAttribute('data-code'));
      const copied = await copyText(code);
      const originalText = 'Copy code';

      copyButton.textContent = copied ? 'Copied ✓' : 'Copy failed';
      window.setTimeout(() => {
        copyButton.textContent = originalText;
      }, 1600);
      return;
    }

    const applyButton = target.closest('[data-cb-discount-apply]');
    if (applyButton) {
      event.preventDefault();

      const widget = applyButton.closest('[data-cb-discount]');
      const input = widget?.querySelector('[data-cb-discount-input]');
      await applyCode(normalizeCode(input?.value));
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'CartDrawer-Form' && form.id !== 'cart') return;

    const submitter = event.submitter;
    if (submitter && submitter.getAttribute('name') !== 'checkout') return;

    const scope = form.id === 'CartDrawer-Form'
      ? form.closest('cart-drawer')
      : document.getElementById('main-cart-footer');

    const widget = scope?.querySelector('[data-cb-discount]');
    const input = widget?.querySelector('[data-cb-discount-input]');
    const code = normalizeCode(input?.value);

    event.preventDefault();

    if (code) {
      const valid = await applyCode(code);
      if (!valid) return;
    }

    window.location.assign('/checkout');
  }, true);

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh({ cartChanged: true }), 100);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
  }

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(scheduleRefresh).catch(() => {});
    } else {
      scheduleRefresh();
    }
  });

  document.addEventListener('shopify:cart:discount-update', (event) => {
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => refresh()).catch(() => {});
    } else {
      refresh();
    }
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (
          node.matches('[data-cb-discount], .drawer__footer, #main-cart-footer') ||
          node.querySelector('[data-cb-discount]')
        ) {
          window.setTimeout(() => {
            setInputs(draftCode);
            refresh();
          }, 0);
          return;
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  const init = () => refresh();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();